/**
 * PM List watcher helpers
 * @module services/pm-list/watchers-helpers
 */
import _get from 'lodash/get'
import { Map } from 'immutable'
import {
  client as apolloClient,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
  APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
} from 'services/apollo-graphql'
import {
  setPmList as setPmListAction,
  setPmListError as setPmListErrorAction,
  setPmListProcessing as setPmListProcessingAction,
} from './actions'
import GET_PM_LIST_QUERY from './query'
import {
  getPmListProcessing,
  getPmListErrorCreated,
  getPmListDataCreated,
  GET_PM_LIST_ERROR_CACHE_TIME,
} from '.'

/**
 * Get a PM List and dispatch actions
 * @param {Object} options The options
 * @param {import('redux').Dispatch} options.dispatch A redux dispatch function
 * @param {Map} options.auth The auth state
 * @param {Map} options.pmList The PM List state
 * @param {String} options.listId The PM List ID
 * @param {String} options.language The requested language
 * @param {Date} options.now The current date
 */
async function getPmList (options = {}) {
  const {
    auth = Map(),
    pmList = Map(),
    dispatch,
    listId,
    // eslint-disable-next-line no-unused-vars
    language, // Currently unused but passed from watcher
    now = new Date(),
  } = options
  try {
    const processing = getPmListProcessing({ pmList, listId })
    const errorCreated = getPmListErrorCreated({ pmList, listId })
    const dataCreated = getPmListDataCreated({ pmList, listId })

    if (processing) {
      return
    }
    if (errorCreated && now.getTime() < (errorCreated.getTime() + GET_PM_LIST_ERROR_CACHE_TIME)) {
      return
    }
    if (dataCreated && now.getTime() < (dataCreated.getTime() + (60 * 1000))) {
      return
    }

    dispatch(setPmListProcessingAction(listId, true))
    const authToken = auth.get('jwt')

    const { data, error } = await apolloClient.query({
      query: GET_PM_LIST_QUERY,
      variables: {
        listId,
      },
      fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
      errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
      context: {
        jwt: authToken,
      },
    })

    if (error) {
      throw error
    }

    const pmListData = _get(data, ['pmListList', 'pmLists', 0])
    if (!pmListData) {
      throw new Error(`No pmList found for id: ${listId}`)
    }

    dispatch(setPmListAction({
      listId,
      data: pmListData,
      processing: false,
    }))
  } catch (e) {
    dispatch(setPmListErrorAction(listId, e))
  }
}

export default getPmList
